
"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { User, onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import type { AppUser, Store, ShopInfoState } from '@/types';
import { createDefaultShopInfoState } from '@/types';
import { auth, getUserProfile, getStoresForUser, getShopSettings, createUserProfile } from '@/lib/firebase';
import { Loader2 } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";

interface AuthContextType {
  firebaseUser: User | null;
  appUser: AppUser | null;
  currentStoreId: string | null;
  userStores: Store[];
  isLoadingAuth: boolean;
  shopSettings: ShopInfoState; // Guaranteed to be an object
  isLoadingSettings: boolean;
  setCurrentStoreId: (storeId: string | null) => void;
  loginWithEmail: (email: string, password: string) => Promise<User>;
  logout: () => void;
  reloadUserInfo: () => Promise<void>;
  reloadShopSettings: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  firebaseUser: null,
  appUser: null,
  currentStoreId: null,
  userStores: [],
  isLoadingAuth: true,
  shopSettings: createDefaultShopInfoState(),
  isLoadingSettings: true,
  setCurrentStoreId: () => {},
  loginWithEmail: async () => { throw new Error("loginWithEmail function not ready"); },
  logout: () => {},
  reloadUserInfo: async () => {},
  reloadShopSettings: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [currentStoreId, setCurrentStoreIdState] = useState<string | null>(null);
  const [userStores, setUserStores] = useState<Store[]>([]);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [shopSettings, setShopSettings] = useState<ShopInfoState>(createDefaultShopInfoState());
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  const router = useRouter();
  const pathname = usePathname();
  const { toast } = useToast();
  
  const loginWithEmail = async (email: string, password: string): Promise<User> => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  };

  const handleSignOut = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout error:", error);
      toast({ title: "Logout Failed", description: "Could not sign you out.", variant: "destructive" });
    } finally {
      // Reset all state
      setFirebaseUser(null);
      setAppUser(null);
      setCurrentStoreIdState(null);
      setUserStores([]);
      setShopSettings(createDefaultShopInfoState());
      // Clear session storage and redirect
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('swiftserve-store');
        sessionStorage.removeItem('swiftserve-kioskMode');
        window.location.assign('/');
      }
    }
  }, [toast]);
  
  const reloadUserInfo = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
        const profile = await getUserProfile(user.uid);
        setAppUser(profile);
        if (profile && Object.keys(profile.accessibleStores).length > 0) {
            const storeIds = Object.keys(profile.accessibleStores);
            const stores = await getStoresForUser(storeIds);
            setUserStores(stores);
        } else {
            setUserStores([]);
            setCurrentStoreIdState(null);
        }
    } catch (error) {
        console.error("Error reloading user info:", error);
        toast({ title: "Error", description: "Could not refresh user information.", variant: "destructive" });
    }
  }, [toast]);
  
  const reloadShopSettings = useCallback(async () => {
    setIsLoadingSettings(true);
    if (!currentStoreId) {
      setShopSettings(createDefaultShopInfoState());
      setIsLoadingSettings(false);
      return;
    }
    try {
        const settings = await getShopSettings(currentStoreId);
        setShopSettings(settings);
    } catch (error) {
        console.error("Error reloading shop settings:", error);
        toast({ title: "Error", description: "Could not refresh shop settings.", variant: "destructive" });
        setShopSettings(createDefaultShopInfoState());
    } finally {
        setIsLoadingSettings(false);
    }
  }, [currentStoreId, toast]);

  // Combined initialization and auth effect
  useEffect(() => {
    // This is a critical guard. If the auth object from firebase isn't ready,
    // we should not proceed, as it will cause a crash.
    if (!auth) {
        console.error("Firebase Auth is not configured. AuthProvider cannot initialize.");
        setIsLoadingAuth(false);
        return;
    }
      
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          setFirebaseUser(user);
          let profile = await getUserProfile(user.uid);

          const MASTER_ADMIN_UID = 'zYzSK5UfxgWKJOHDCTYrap0GTQT2';
          if (!profile && user.uid === MASTER_ADMIN_UID) {
            console.log("Master Admin profile missing. Self-healing...");
            try {
              const masterAdminData = {
                email: user.email,
                fullName: 'Master Admin',
                accessibleStores: {},
                pin: '78601'
              };
              await createUserProfile(user.uid, masterAdminData);
              profile = await getUserProfile(user.uid);
              toast({
                title: "Master Account Repaired",
                description: "Your master admin profile was automatically repaired."
              });
            } catch (creationError) {
              console.error("CRITICAL: Failed to create missing master admin profile:", creationError);
              toast({ title: "Repair Failed", description: "Could not repair the master admin account.", variant: "destructive" });
              await handleSignOut();
              return;
            }
          } else if (!profile) {
              console.warn(`Profile for UID ${user.uid} not found. Retrying in 2s.`);
              await new Promise(resolve => setTimeout(resolve, 2000));
              profile = await getUserProfile(user.uid);
          }
          
          if (profile) {
              setAppUser(profile);
              const storeIds = Object.keys(profile.accessibleStores);
              if (storeIds.length > 0) {
                const stores = await getStoresForUser(storeIds);
                setUserStores(stores);
                const savedStoreId = sessionStorage.getItem('swiftserve-store');
                if (savedStoreId && stores.some(s => s.id === savedStoreId)) {
                  setCurrentStoreIdState(savedStoreId);
                } else {
                  const firstStoreId = stores[0].id;
                  setCurrentStoreIdState(firstStoreId);
                  sessionStorage.setItem('swiftserve-store', firstStoreId);
                }
              } else {
                 setUserStores([]);
                 setCurrentStoreIdState(null);
              }
          } else {
              console.error(`CRITICAL: User is authenticated (UID: ${user.uid}) but no profile document was found in Firestore.`);
              toast({
                  title: "Profile Load Failed",
                  description: "Your user data could not be found. Please contact an administrator.",
                  variant: "destructive",
                  duration: 10000,
              });
          }

          setIsLoadingAuth(false);
        } else {
          // User is signed out
          setFirebaseUser(null);
          setAppUser(null);
          setCurrentStoreIdState(null);
          setUserStores([]);
          setShopSettings(createDefaultShopInfoState());
          setIsLoadingAuth(false);
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        toast({ title: "Authentication Error", description: "An error occurred during login. Please try again.", variant: "destructive" });
        await handleSignOut(); // Sign out on error to be safe
      }
    });

    return () => unsubscribe();
  }, [handleSignOut, toast]);

  // Effect for fetching shop settings when storeId changes
  useEffect(() => {
    if (currentStoreId) {
      reloadShopSettings();
    } else {
      setShopSettings(createDefaultShopInfoState());
      setIsLoadingSettings(false);
    }
  }, [currentStoreId, reloadShopSettings]);
  
  // Routing effect
  useEffect(() => {
    // Wait until authentication and profile loading are fully resolved.
    if (isLoadingAuth || (firebaseUser && !appUser)) {
      return; 
    }

    const publicPaths = ['/', '/signup', '/queue'];
    const isPublicPath = publicPaths.includes(pathname) || pathname.startsWith('/kiosk');
    
    // --- Handle Logged-Out Users ---
    if (!firebaseUser) {
        if (!isPublicPath) {
            router.replace('/');
        }
        return;
    }

    // --- User and profile are guaranteed to be loaded ---
    const isMasterAdmin = firebaseUser.uid === 'zYzSK5UfxgWKJOHDCTYrap0GTQT2';
    const authPages = ['/', '/signup'];
    
    // Rule 1: If logged in, redirect away from auth pages.
    if (authPages.includes(pathname)) {
        router.replace('/dashboard');
        return;
    }

    // Rule 2: Handle the /create-store page specifically.
    if (pathname === '/create-store') {
        // A regular user who already has stores should not be on this page.
        if (!isMasterAdmin && userStores.length > 0) {
            router.replace('/dashboard');
        }
        // Master admin and new users without stores are allowed, so we do nothing here and exit the effect.
        return;
    }
    
    // Rule 3: A new user (not master admin, no stores) MUST be on the create page.
    // If they try to access anything else, redirect them.
    if (!isMasterAdmin && userStores.length === 0) {
        if (pathname !== '/create-store') {
            router.replace('/create-store');
        }
    }
    
  }, [isLoadingAuth, firebaseUser, appUser, userStores, pathname, router]);


  const setCurrentStoreId = useCallback((storeId: string | null) => {
    if (currentStoreId === storeId) return;

    if (storeId) {
      sessionStorage.setItem('swiftserve-store', storeId);
    } else {
      sessionStorage.removeItem('swiftserve-store');
    }
    
    setCurrentStoreIdState(storeId);
    router.push('/dashboard');
  }, [currentStoreId, router]);

  const value = useMemo(() => ({
    firebaseUser,
    appUser,
    currentStoreId,
    userStores,
    isLoadingAuth,
    shopSettings,
    isLoadingSettings,
    setCurrentStoreId,
    loginWithEmail,
    logout: handleSignOut,
    reloadUserInfo,
    reloadShopSettings,
  }), [
    firebaseUser,
    appUser,
    currentStoreId,
    userStores,
    isLoadingAuth,
    shopSettings,
    isLoadingSettings,
    setCurrentStoreId,
    handleSignOut,
    reloadUserInfo,
    reloadShopSettings,
  ]);
  
  const isAuthDependentPage = !pathname.startsWith('/kiosk') && !['/', '/signup', '/queue'].includes(pathname);
  if (isLoadingAuth && isAuthDependentPage) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-secondary">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4 text-lg text-muted-foreground">Initializing Application...</p>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
